import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AuthenticatedImage from '../../core/AuthenticatedImage';
import { BlogImage } from '../blogSchemas';
import { EVENT_DIALOG_MAX_WIDTH, EVENT_DIALOG_PAPER_SX } from '../../calendar/dialogLayout';
import { secondaryButtonSx, dangerButtonSx } from '../../core/pvtaStyles';

interface BlogImageGalleryDialogProps {
    open: boolean;
    images: BlogImage[];
    currentUserId: number | null;
    initialSelectedIndex?: number | null;
    isDeleting: boolean;
    isUploading: boolean;
    isDownloadingZip: boolean;
    onClose: () => void;
    onDelete: (imageId: number) => Promise<void>;
    onDownload: (image: BlogImage) => Promise<void>;
    onDownloadZip: (imageIds: number[]) => Promise<void>;
    onUpload: (files: File[]) => Promise<void>;
}

const getDisplayTitle = (image: BlogImage): string => image.caption.trim() || image.originalFilename;

const formatDateTime = (value?: string | null): string => {
    if (!value) {
        return '-';
    }
    return new Intl.DateTimeFormat('de-DE', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(new Date(value));
};

const BlogImageGalleryDialog: React.FC<BlogImageGalleryDialogProps> = ({
    open,
    images,
    currentUserId,
    initialSelectedIndex = null,
    isDeleting,
    isUploading,
    isDownloadingZip,
    onClose,
    onDelete,
    onDownload,
    onDownloadZip,
    onUpload,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const uploadInputRef = useRef<HTMLInputElement | null>(null);

    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set<number>());
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [menuImageId, setMenuImageId] = useState<number | null>(null);
    const [actionImageId, setActionImageId] = useState<number | null>(null);
    const [infoOpen, setInfoOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const selectedImage = useMemo(() => {
        if (selectedIndex === null) {
            return null;
        }
        return images[selectedIndex] ?? null;
    }, [images, selectedIndex]);

    const menuImage = useMemo(() => (
        menuImageId === null ? null : images.find((image) => image.id === menuImageId) ?? null
    ), [images, menuImageId]);
    const actionImage = useMemo(() => (
        actionImageId === null ? null : images.find((image) => image.id === actionImageId) ?? null
    ), [actionImageId, images]);

    const canGoPrev = selectedIndex !== null && selectedIndex > 0;
    const canGoNext = selectedIndex !== null && selectedIndex < images.length - 1;
    const selectedCount = selectedIds.size;

    const isOwner = (image: BlogImage): boolean => currentUserId !== null && currentUserId === image.uploader?.id;

    const handleCloseMenu = (): void => {
        setMenuAnchorEl(null);
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, imageId: number): void => {
        setMenuAnchorEl(event.currentTarget);
        setMenuImageId(imageId);
    };

    const handlePickUploadFiles = (): void => {
        if (!isUploading) {
            uploadInputRef.current?.click();
        }
    };

    const handleUploadChange = async ({ target }: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const selectedFiles = target.files ? Array.from(target.files) : [];
        if (selectedFiles.length > 0) {
            await onUpload(selectedFiles);
        }
        target.value = '';
    };

    useEffect(() => {
        if (!open) {
            setSelectedIndex(null);
            setSelectedIds(new Set<number>());
            setMenuAnchorEl(null);
            setMenuImageId(null);
            setActionImageId(null);
            setInfoOpen(false);
            setDeleteConfirmOpen(false);
            return;
        }

        if (initialSelectedIndex !== null && initialSelectedIndex !== undefined && images[initialSelectedIndex]) {
            setSelectedIndex(initialSelectedIndex);
        }
    }, [images, initialSelectedIndex, open]);

    useEffect(() => {
        setSelectedIds((previous) => {
            const availableIds = new Set(images.map((image) => image.id));
            const next = new Set<number>();
            previous.forEach((id) => {
                if (availableIds.has(id)) {
                    next.add(id);
                }
            });
            return next;
        });
    }, [images]);

    useEffect(() => {
        if (selectedIndex !== null && !images[selectedIndex]) {
            setSelectedIndex(null);
        }
    }, [images, selectedIndex]);

    useEffect(() => {
        if (!menuAnchorEl) {
            setMenuImageId(null);
        }
    }, [menuAnchorEl]);

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                fullScreen={isMobile}
                maxWidth={EVENT_DIALOG_MAX_WIDTH}
                PaperProps={EVENT_DIALOG_PAPER_SX}
            >
                <DialogTitle sx={{ px: { xs: 1.25, sm: 2.5 }, py: { xs: 1, sm: 1.5 } }}>
                    {isMobile ? (
                        <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                                    {`Alle Fotos (${images.length})`}
                                </Typography>
                                <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
                                    <Tooltip title="Hochladen">
                                        <span>
                                            <IconButton size="small" onClick={handlePickUploadFiles} disabled={isUploading}>
                                                <AddPhotoAlternateIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <IconButton size="small" onClick={onClose}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                            {selectedCount > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {`${selectedCount} von ${images.length} ausgewählt`}
                                </Typography>
                            )}
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => setSelectedIds(new Set(images.map((image) => image.id)))}
                                    disabled={images.length === 0 || selectedCount === images.length}
                                    sx={{ minWidth: 0, px: 0.75 }}
                                >
                                    Alle
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => setSelectedIds(new Set<number>())}
                                    disabled={selectedCount === 0}
                                    sx={{ minWidth: 0, px: 0.75 }}
                                >
                                    Aufheben
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DownloadIcon fontSize="small" />}
                                    onClick={() => void onDownloadZip(Array.from(selectedIds))}
                                    disabled={selectedCount === 0 || isDownloadingZip}
                                    sx={{ minWidth: 0, px: 0.75 }}
                                >
                                    ZIP
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
                            <Stack sx={{ minWidth: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                                    {`Alle Fotos (${images.length})`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {`${selectedCount} von ${images.length} Fotos ausgewählt`}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => void onDownloadZip(Array.from(selectedIds))}
                                    disabled={selectedCount === 0 || isDownloadingZip}
                                >
                                    Auswahl herunterladen (ZIP)
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => setSelectedIds(new Set(images.map((image) => image.id)))}
                                    disabled={images.length === 0 || selectedCount === images.length}
                                >
                                    Alle auswählen
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => setSelectedIds(new Set<number>())}
                                    disabled={selectedCount === 0}
                                >
                                    Auswahl aufheben
                                </Button>
                                <Tooltip title="Hochladen">
                                    <span>
                                        <IconButton onClick={handlePickUploadFiles} disabled={isUploading}>
                                            <AddPhotoAlternateIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <IconButton onClick={onClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                    )}
                </DialogTitle>
                <DialogContent dividers sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 }, overflowX: 'hidden' }}>
                    <input
                        ref={uploadInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        hidden
                        onChange={(event) => void handleUploadChange(event)}
                    />
                    <Box
                        sx={{
                            width: '100%',
                            display: 'grid',
                            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                            gap: { xs: 0.75, sm: 1.25 },
                        }}
                    >
                        {images.map((image, index) => (
                            <Card key={image.id} variant="outlined">
                                <Box sx={{ position: 'relative' }}>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: { xs: 4, sm: 6 },
                                            left: { xs: 4, sm: 6 },
                                            zIndex: 2,
                                            width: { xs: 22, sm: 26 },
                                            height: { xs: 22, sm: 26 },
                                            borderRadius: '50%',
                                            bgcolor: 'rgba(255,255,255,0.85)',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backdropFilter: 'blur(1px)',
                                        }}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={selectedIds.has(image.id)}
                                            onChange={() => {
                                                setSelectedIds((previous) => {
                                                    const next = new Set(previous);
                                                    if (next.has(image.id)) {
                                                        next.delete(image.id);
                                                    } else {
                                                        next.add(image.id);
                                                    }
                                                    return next;
                                                });
                                            }}
                                            sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: { xs: 16, sm: 18 } } }}
                                        />
                                    </Box>
                                    <CardActionArea onClick={() => setSelectedIndex(index)}>
                                        <AuthenticatedImage
                                            url={image.url}
                                            alt={getDisplayTitle(image)}
                                            sx={{ width: '100%', height: { xs: 96, sm: 140 }, objectFit: 'cover' }}
                                        />
                                    </CardActionArea>
                                </Box>
                                <CardContent sx={{ py: 0.5, px: 0.75, '&:last-child': { pb: 0.5 } }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                        <Typography variant="caption" sx={{ minWidth: 0 }} noWrap>
                                            {getDisplayTitle(image)}
                                        </Typography>
                                        <Stack direction="row" spacing={0.25}>
                                            <IconButton size="small" onClick={() => void onDownload(image)}>
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={(event) => handleOpenMenu(event, image.id)}>
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog
                open={selectedImage !== null}
                onClose={() => setSelectedIndex(null)}
                fullWidth
                fullScreen={isMobile}
                maxWidth={EVENT_DIALOG_MAX_WIDTH}
                PaperProps={EVENT_DIALOG_PAPER_SX}
            >
                <DialogTitle sx={{ px: { xs: 1, sm: 2.5 }, py: { xs: 0.75, sm: 1.5 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, minWidth: 0, pr: 1 }} noWrap>
                        {selectedImage ? getDisplayTitle(selectedImage) : ''}
                    </Typography>
                    <Stack direction="row" spacing={0.25}>
                        {selectedImage && (
                            <IconButton size="small" onClick={() => void onDownload(selectedImage)}>
                                <DownloadIcon fontSize="small" />
                            </IconButton>
                        )}
                        {selectedImage && (
                            <IconButton size="small" onClick={(event) => handleOpenMenu(event, selectedImage.id)}>
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        )}
                        <IconButton size="small" onClick={() => setSelectedIndex(null)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers sx={{ p: { xs: 0.75, sm: 2 }, overflowX: 'hidden' }}>
                    {selectedImage && (
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                minHeight: { xs: 'calc(100vh - 112px)', sm: '72vh' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'background.default',
                                borderRadius: 1.5,
                            }}
                        >
                            {!isMobile && (
                                <IconButton
                                    onClick={() => setSelectedIndex((index) => (index === null ? null : index - 1))}
                                    disabled={!canGoPrev}
                                    sx={{ position: 'absolute', left: 8, zIndex: 2 }}
                                >
                                    <NavigateBeforeIcon />
                                </IconButton>
                            )}

                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', px: { xs: 0.5, sm: 6 } }}>
                                <AuthenticatedImage
                                    url={selectedImage.url}
                                    alt={getDisplayTitle(selectedImage)}
                                    sx={{
                                        width: '100%',
                                        maxWidth: '100%',
                                        maxHeight: { xs: 'calc(100vh - 168px)', sm: '65vh' },
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>

                            {!isMobile && (
                                <IconButton
                                    onClick={() => setSelectedIndex((index) => (index === null ? null : index + 1))}
                                    disabled={!canGoNext}
                                    sx={{ position: 'absolute', right: 8, zIndex: 2 }}
                                >
                                    <NavigateNextIcon />
                                </IconButton>
                            )}

                            {isMobile && (
                                <>
                                    <IconButton
                                        onClick={() => setSelectedIndex((index) => (index === null ? null : index - 1))}
                                        disabled={!canGoPrev}
                                        sx={{
                                            position: 'absolute',
                                            left: 6,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            zIndex: 2,
                                            bgcolor: 'background.paper',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            width: 34,
                                            height: 34,
                                            '&:hover': { bgcolor: 'background.paper' },
                                        }}
                                    >
                                        <NavigateBeforeIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => setSelectedIndex((index) => (index === null ? null : index + 1))}
                                        disabled={!canGoNext}
                                        sx={{
                                            position: 'absolute',
                                            right: 6,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            zIndex: 2,
                                            bgcolor: 'background.paper',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            width: 34,
                                            height: 34,
                                            '&:hover': { bgcolor: 'background.paper' },
                                        }}
                                    >
                                        <NavigateNextIcon fontSize="small" />
                                    </IconButton>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
                <MenuItem
                    onClick={() => {
                        setActionImageId(menuImage?.id ?? null);
                        setInfoOpen(true);
                        handleCloseMenu();
                    }}
                >
                    Informationen
                </MenuItem>
                {menuImage && isOwner(menuImage) && (
                    <MenuItem
                        onClick={() => {
                            setActionImageId(menuImage.id);
                            setDeleteConfirmOpen(true);
                            handleCloseMenu();
                        }}
                        disabled={isDeleting}
                    >
                        Löschen
                    </MenuItem>
                )}
            </Menu>

            <Dialog
                open={infoOpen}
                onClose={() => {
                    setInfoOpen(false);
                    setActionImageId(null);
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Bildinformationen</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Titel</Typography>
                            <Typography variant="body2">{actionImage ? getDisplayTitle(actionImage) : '-'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Datei</Typography>
                            <Typography variant="body2">{actionImage?.originalFilename ?? '-'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Hochgeladen von</Typography>
                            <Typography variant="body2">{actionImage?.uploader?.name ?? '-'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Hochgeladen am</Typography>
                            <Typography variant="body2">{formatDateTime(actionImage?.uploadedAt)}</Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setInfoOpen(false);
                        setActionImageId(null);
                    }}
                    >
                        Schließen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => {
                    setDeleteConfirmOpen(false);
                    setActionImageId(null);
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Bild löschen?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">Diese Aktion kann nicht rückgängig gemacht werden.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={secondaryButtonSx}
                        onClick={() => {
                            setDeleteConfirmOpen(false);
                            setActionImageId(null);
                        }}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        sx={dangerButtonSx}
                        disabled={isDeleting || !actionImage}
                        onClick={async () => {
                            if (!actionImage) {
                                return;
                            }
                            await onDelete(actionImage.id);
                            setDeleteConfirmOpen(false);
                            setActionImageId(null);
                            if (selectedImage?.id === actionImage.id) {
                                setSelectedIndex(null);
                            }
                        }}
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BlogImageGalleryDialog;
